// src/pages/billing/UsageSummaryPage.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useBillingUsage } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

export const UsageSummaryPage = observer(() => {
  const store = useBillingUsage();
  const [billingAccountId, setBillingAccountId] = useState('');
  const [summaries, setSummaries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    if (!billingAccountId) return;
    setLoading(true);
    try {
      const res = await store.GetUsageSummary({
        billing_account_id: billingAccountId,
      }) as any;
      const list = res?.summaries?.summaries ?? res?.summaries;
      setSummaries(Array.isArray(list) ? list : []);
    } catch {
      // Error handled by store
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Usage Summary</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/usage" className="text-muted-foreground hover:text-foreground">Usage</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Summary</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Input label="Billing Account ID" type="text" value={billingAccountId} onChange={(e) => setBillingAccountId(e.target.value)} placeholder="Enter billing account ID" />
            </div>
            <Button onClick={loadSummary} disabled={loading || !billingAccountId}>
              {loading ? 'Loading...' : 'Load Summary'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {summaries.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Usage by Type</CardTitle></CardHeader>
          <CardContent>
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>Usage Type</TableHead>
                  <TableHead>Total Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Estimated Amount</TableHead>
                  <TableHead>Record Count</TableHead>
                  <TableHead>Period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaries.map((s: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{String(s.usage_type ?? '--')}</TableCell>
                    <TableCell>{String(s.total_quantity ?? '--')}</TableCell>
                    <TableCell>{String(s.unit ?? '--')}</TableCell>
                    <TableCell>{formatMoney(s.estimated_amount)}</TableCell>
                    <TableCell>{String(s.record_count ?? '--')}</TableCell>
                    <TableCell>{formatDate(s.period_start)} - {formatDate(s.period_end)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default UsageSummaryPage;
